import { Badge } from '../../catalyst/badge';
import { Input } from '../../catalyst/input';
import { nodeLabel } from './graphModel';
import type { MatchReason, SearchResult } from './searchGraph';

type Props = {
  query: string;
  onQuery: (query: string) => void;
  results: SearchResult[];
  onPick: (nodeId: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
};

const REASON_TEXT: Record<MatchReason, string> = { exact: 'exact', prefix: 'starts with', contains: 'contains' };

/** Searches the graph by identifier, best matches first; picking a result focuses the view on it. */
export function GraphSearch({ query, onQuery, results, onPick, inputRef }: Props) {
  return (
    <div className="text-sm text-zinc-900 dark:text-zinc-100">
      <Input
        ref={inputRef}
        type="search"
        aria-label="Search the graph"
        placeholder="ESN, serial, part number, AD…"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      {query.trim() !== '' && results.length === 0 && (
        <p className="mt-1 text-zinc-500 dark:text-zinc-400">No matches.</p>
      )}
      {results.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
          {results.map(({ node, reason }) => (
            <li key={node.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-zinc-100 dark:hover:bg-zinc-700"
                onClick={() => onPick(node.id)}
              >
                <Badge color="zinc" className="shrink-0">
                  {node.kind}
                </Badge>
                {/* Two lines, so sibling tasks whose descriptions differ late stay apart. */}
                <span className="line-clamp-2 min-w-0 flex-1 break-words" title={nodeLabel(node)}>
                  {nodeLabel(node)}
                </span>
                <span className="shrink-0 text-xs text-zinc-500 dark:text-zinc-400">{REASON_TEXT[reason]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
